import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFinance } from "@/context/FinanceContext";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Wallet, TrendingUp, Calendar, AlertTriangle } from "lucide-react";
import { 
  formatCurrencyCompact,
  getOverdueSummary,
  getTodaySummary,
  getOpenSummary,
  calculateCurrentBalance,
  getPendingTransactions
} from "@/utils/financeCalculations";

/**
 * PaymentsReceipts Component
 * @description Displays summary of pending payments and receivables
 * organized by overdue, today, and future periods
 */
export function PaymentsReceipts() {
  const {
    transactions,
    accounts
  } = useFinance();
  const navigate = useNavigate();

  const paymentData = useMemo(() => {
    const currentBalance = calculateCurrentBalance(accounts, transactions);
    const pendingTransactions = transactions.filter(t => !t.settled);
    const { receivables: allReceivables, payables: allPayables } = getPendingTransactions(transactions);

    // Get summaries by period
    const overdueReceivables = getOverdueSummary(pendingTransactions, 'receita');
    const overduePayables = getOverdueSummary(pendingTransactions, 'despesa');
    const todayReceivables = getTodaySummary(pendingTransactions, 'receita');
    const todayPayables = getTodaySummary(pendingTransactions, 'despesa');
    
    // Em Aberto = future + no due date (not overdue, not today)
    const openReceivables = getOpenSummary(pendingTransactions, 'receita');
    const openPayables = getOpenSummary(pendingTransactions, 'despesa');

    // Calculate totals (Vencido + Hoje + Em Aberto = Total)
    const totalReceivables = overdueReceivables.total + todayReceivables.total + openReceivables.total;
    const totalPayables = overduePayables.total + todayPayables.total + openPayables.total;
    const totalBalance = totalReceivables - totalPayables;
    const todayMovements = todayReceivables.total - todayPayables.total;
    const projectedTodayBalance = currentBalance + todayMovements;
    const openBalance = openReceivables.total - openPayables.total;

    return {
      todayReceivablesTotal: todayReceivables.total,
      todayPayablesTotal: todayPayables.total,
      overdueReceivablesTotal: overdueReceivables.total,
      overduePayablesTotal: overduePayables.total,
      openReceivablesTotal: openReceivables.total,
      openPayablesTotal: openPayables.total,
      totalReceivables,
      totalPayables,
      totalBalance,
      todayMovements,
      projectedTodayBalance,
      currentBalance,
      openBalance,
      todayReceivables: todayReceivables.transactions,
      todayPayables: todayPayables.transactions,
      overdueReceivables: overdueReceivables.transactions,
      overduePayables: overduePayables.transactions,
      openReceivables: openReceivables.transactions,
      openPayables: openPayables.transactions,
      allReceivables,
      allPayables
    };
  }, [transactions, accounts]);
  /**
   * Navigates to transactions page with specific transaction IDs
   * When empty, uses a special marker to show empty results
   */
  const handleNavigateWithTransactionIds = (transactionIds: string[]) => {
    const baseUrl = '/lancamentos';
    const params = new URLSearchParams();
    // Use special marker 'empty' when no transactions, so the filter shows zero results
    params.set('transactionIds', transactionIds.length > 0 ? transactionIds.join(',') : 'empty');
    navigate(`${baseUrl}?${params.toString()}`);
  };
  
  /**
   * Navigates to transactions page with filtered transactions by period
   */
  const handleNavigateToTransactions = (type: 'today' | 'overdue' | 'open' | 'all') => {
    let transactionIds: string[] = [];
    if (type === 'today') {
      // Combine both today receivables and payables
      transactionIds = [...paymentData.todayReceivables.map(t => t.id), ...paymentData.todayPayables.map(t => t.id)];
    } else if (type === 'overdue') {
      // Combine both overdue receivables and payables
      transactionIds = [...paymentData.overdueReceivables.map(t => t.id), ...paymentData.overduePayables.map(t => t.id)];
    } else if (type === 'open') {
      // Combine both open receivables and payables (future + no due date)
      transactionIds = [...paymentData.openReceivables.map(t => t.id), ...paymentData.openPayables.map(t => t.id)];
    } else if (type === 'all') {
      // Combine all receivables and payables
      transactionIds = [...paymentData.allReceivables.map(t => t.id), ...paymentData.allPayables.map(t => t.id)];
    }
    handleNavigateWithTransactionIds(transactionIds);
  };

  // Helper component for aligned currency display
  const AlignedCurrency = ({ value, className }: { value: number; className?: string }) => {
    const isNegative = value < 0;
    const formattedNumber = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(value));
    
    return (
      <span className={`inline-flex items-center justify-center w-full ${className || ''}`}>
        <span className="w-3 text-left flex-shrink-0">{isNegative ? '-' : ''}</span>
        <span className="tabular-nums">{formattedNumber}</span>
      </span>
    );
  };

  return <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <CalendarDays className="h-5 w-5" />
          Pagamentos e recebimentos
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-4 px-4 space-y-4">
        {/* Summary cards primeiro */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Wallet, label: "Saldo atual", value: paymentData.currentBalance },
            { icon: TrendingUp, label: "Movimentações hoje", value: paymentData.todayMovements },
            { icon: CalendarDays, label: "Saldo previsto hoje", value: paymentData.projectedTodayBalance },
            { icon: Calendar, label: "Em Aberto", value: paymentData.openBalance },
          ].map((item) => (
            <div key={item.label} className="flex flex-col items-center text-center gap-1.5 py-2.5 px-2 rounded-lg hover:bg-muted/40 transition-colors">
              <div className="h-8 w-8 rounded-full bg-primary/8 flex items-center justify-center shrink-0">
                <item.icon className="h-4 w-4 text-primary" />
              </div>
              <p className="text-xs text-muted-foreground leading-tight">{item.label}</p>
              <p className={`text-sm font-semibold tabular-nums ${item.value >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                {formatCurrencyCompact(item.value)}
              </p>
            </div>
          ))}
        </div>

        {/* Grid de períodos */}
        <div className="space-y-1 pt-2 border-t border-border/40">
          {/* Header row */}
          <div className="grid grid-cols-5 gap-2 px-3 pb-2">
            <div />
            <span className="text-xs font-medium text-muted-foreground text-center">Vencido</span>
            <span className="text-xs font-medium text-muted-foreground text-center">Hoje</span>
            <span className="text-xs font-medium text-muted-foreground text-center">Em Aberto</span>
            <span className="text-xs font-medium text-muted-foreground text-center">Total</span>
          </div>

          {/* Receitas row */}
          <div className="grid grid-cols-5 gap-2 items-center py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors border-b border-border/40">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
              <span className="text-sm text-foreground/80">Receitas</span>
            </div>
            <span className="text-center text-sm font-medium tabular-nums text-emerald-600 dark:text-emerald-400 cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.overdueReceivables.map(t => t.id))}>
              <AlignedCurrency value={paymentData.overdueReceivablesTotal} />
            </span>
            <span className="text-center text-sm font-medium tabular-nums text-emerald-600 dark:text-emerald-400 cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.todayReceivables.map(t => t.id))}>
              <AlignedCurrency value={paymentData.todayReceivablesTotal} />
            </span>
            <span className="text-center text-sm font-medium tabular-nums text-emerald-600 dark:text-emerald-400 cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.openReceivables.map(t => t.id))}>
              <AlignedCurrency value={paymentData.openReceivablesTotal} />
            </span>
            <span className="text-center text-sm font-medium tabular-nums text-emerald-600 dark:text-emerald-400 cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.allReceivables.map(t => t.id))}>
              <AlignedCurrency value={paymentData.totalReceivables} />
            </span>
          </div>

          {/* Despesas row */}
          <div className="grid grid-cols-5 gap-2 items-center py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors border-b border-border/40">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-destructive shrink-0" />
              <span className="text-sm text-foreground/80">Despesas</span>
            </div>
            <span className="text-center text-sm font-medium tabular-nums text-destructive cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.overduePayables.map(t => t.id))}>
              <AlignedCurrency value={-paymentData.overduePayablesTotal} />
            </span>
            <span className="text-center text-sm font-medium tabular-nums text-destructive cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.todayPayables.map(t => t.id))}>
              <AlignedCurrency value={-paymentData.todayPayablesTotal} />
            </span>
            <span className="text-center text-sm font-medium tabular-nums text-destructive cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.openPayables.map(t => t.id))}>
              <AlignedCurrency value={-paymentData.openPayablesTotal} />
            </span>
            <span className="text-center text-sm font-medium tabular-nums text-destructive cursor-pointer hover:underline" onClick={() => handleNavigateWithTransactionIds(paymentData.allPayables.map(t => t.id))}>
              <AlignedCurrency value={-paymentData.totalPayables} />
            </span>
          </div>

          {/* Total row */}
          <div className="grid grid-cols-5 gap-2 items-center py-2.5 px-3 rounded-lg bg-muted/30">
            <span className="text-sm font-semibold text-foreground">Total</span>
            <span className={`text-center text-sm font-bold tabular-nums cursor-pointer hover:underline ${paymentData.overdueReceivablesTotal - paymentData.overduePayablesTotal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`} onClick={() => handleNavigateToTransactions('overdue')}>
              <AlignedCurrency value={paymentData.overdueReceivablesTotal - paymentData.overduePayablesTotal} />
            </span>
            <span className={`text-center text-sm font-bold tabular-nums cursor-pointer hover:underline ${paymentData.todayReceivablesTotal - paymentData.todayPayablesTotal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`} onClick={() => handleNavigateToTransactions('today')}>
              <AlignedCurrency value={paymentData.todayReceivablesTotal - paymentData.todayPayablesTotal} />
            </span>
            <span className={`text-center text-sm font-bold tabular-nums cursor-pointer hover:underline ${paymentData.openBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`} onClick={() => handleNavigateToTransactions('open')}>
              <AlignedCurrency value={paymentData.openBalance} />
            </span>
            <span className={`text-center text-sm font-bold tabular-nums cursor-pointer hover:underline ${paymentData.totalBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`} onClick={() => handleNavigateToTransactions('all')}>
              <AlignedCurrency value={paymentData.totalBalance} />
            </span>
          </div>

          {/* Hint */}
          <div className="flex items-center justify-center gap-2 pt-3 pb-1 px-3 rounded-md bg-primary/5 border border-primary/10">
            <AlertTriangle className="h-4 w-4 text-primary shrink-0" />
            <p className="text-xs font-medium text-primary/80">
              Clique em qualquer valor da tabela para filtrar os lançamentos correspondentes
            </p>
          </div>
        </div>
      </CardContent>
    </Card>;
}